import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  Tooltip
} from '@chakra-ui/react';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button as MRButton, useToast } from '@morressier/alembic';
import { useUpdateJournalSettings } from 'src/api/mutations';

/** Minimal journal shape needed by activate/deactivate components. */
export interface IToggleableJournal {
  id: string;
  data: { name: string };
  isActive?: boolean;
  canActivate?: boolean;
  canDeactivate?: boolean;
}

interface ActivateDeactivateJournalModalProps {
  journal: IToggleableJournal | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Explicit toggle direction — avoids relying on journal flags alone. */
  action: 'activate' | 'deactivate';
}

const ActivateDeactivateJournalModal = ({
  journal,
  isOpen,
  onClose,
  onSuccess,
  action
}: ActivateDeactivateJournalModalProps) => {
  const toast = useToast();
  const updateJournalSettings = useUpdateJournalSettings(journal?.id ?? '');

  const isActivating = action === 'activate';

  const handleConfirmToggle = async () => {
    if (!journal) return;

    await updateJournalSettings.mutateAsync({
      isActive: action === 'activate'
    });
    toast({
      variant: 'success',
      title: isActivating ? 'Journal Activated' : 'Journal Deactivated',
      description: `${journal.data.name} has been ${isActivating ? 'activated' : 'deactivated'} successfully.`,
      isClosable: true
    });
    onClose();
    onSuccess?.();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDirection="column" gap={4} padding={2}>
            <Box
              fontSize="40px"
              color={isActivating ? 'actionSuccessFill' : 'red.500'}
            >
              <FontAwesomeIcon icon={faCheckCircle} fontSize="42px" />
            </Box>
            <Text textStyle="headingGroup">
              {isActivating ? 'Activate Journal' : 'Deactivate Journal'}
            </Text>
            <Text textStyle="textBody">
              {isActivating ? (
                <>
                  Are you sure you want to activate{' '}
                  <strong>{journal?.data?.name}</strong>? This journal will
                  become available for use.
                </>
              ) : (
                <>
                  Are you sure you want to deactivate{' '}
                  <strong>{journal?.data?.name}</strong>? This journal will no
                  longer be available for new submissions.
                </>
              )}
            </Text>
            <Flex flexDirection="row" gap={2} justifyContent="flex-end">
              <MRButton onClick={onClose} variant="secondary">
                Cancel
              </MRButton>
              <MRButton
                onClick={handleConfirmToggle}
                isLoading={updateJournalSettings.isPending}
              >
                {isActivating ? 'Activate' : 'Deactivate'}
              </MRButton>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

/** Renders an Activate/Deactivate button for use outside of a menu/table context.
 *  The button label adapts based on journal.canActivate / journal.canDeactivate.
 *  When neither action is available the button is disabled with a tooltip. */
interface ActivateDeactivateButtonProps {
  journal: IToggleableJournal;
  onClick: () => void;
  /** Explicit toggle direction — the single source of truth for label + disabled state. */
  action: 'activate' | 'deactivate';
  canActivate?: boolean;
  canDeactivate?: boolean;
  /** When true, the journal has no roles configured and cannot be activated. */
  cocConfigEmpty?: boolean;
  /**
   * When true, an article type has no peer review model yet, which also holds
   * activation back. Checked after `cocConfigEmpty` so role assignment, the
   * earlier requirement, is the one named first.
   */
  missingPeerReviewModel?: boolean;
}

export const ActivateDeactivateJournalButton = ({
  journal,
  onClick,
  action,
  canActivate: canActivateProp,
  canDeactivate: canDeactivateProp,
  cocConfigEmpty = false,
  missingPeerReviewModel = false
}: ActivateDeactivateButtonProps) => {

  const isActivating = action === 'activate';

  const isDisabledDueToCoc = isActivating && cocConfigEmpty;
  const isDisabledDueToPeerReviewModel =
    isActivating && !cocConfigEmpty && missingPeerReviewModel;

  const canToggle = isActivating
    ? (canActivateProp ?? journal?.canActivate === true)
    : (canDeactivateProp ?? journal?.canDeactivate === true);
  const isDisabled =
    isDisabledDueToCoc || isDisabledDueToPeerReviewModel || !canToggle;

  /* Role assignment is the earlier requirement, so it is the one named when it
     and the peer review models would both hold activation back. */
  const tooltipLabel = isDisabledDueToPeerReviewModel
    ? 'Every article type needs a peer review model before you can activate this journal.'
    : isActivating
      ? 'Journal role assignment should be configured before you can activate this journal.'
      : 'This journal has active submissions and cannot be deactivated.';

  const buttonLabel = isActivating ? 'Activate Journal' : 'Deactivate Journal';

  const button = (
    <MRButton
      iconLeft={<FontAwesomeIcon icon={faCheckCircle} />}
      onClick={onClick}
      isDisabled={isDisabled}
      disabled={isDisabled}
    >
      {buttonLabel}
    </MRButton>
  );

  if (isDisabled) {
    return (
      <Tooltip
        hasArrow
        borderRadius={4}
        placement="top"
        label={tooltipLabel}
      >
        <Box>{button}</Box>
      </Tooltip>
    );
  }

  return button;
};

export default ActivateDeactivateJournalModal;
